import { Sun, Cloud, CloudRain, Wind, Flame, CircleDot, TriangleAlert, AlertTriangle, CloudSun } from "lucide-react";

/** Weather options — keys match the backend (`site_diary.WEATHER`). */
export const WEATHER = [
  { key: "sunny", label: "Sunny", icon: Sun },
  { key: "cloudy", label: "Cloudy", icon: Cloud },
  { key: "raining", label: "Raining", icon: CloudRain },
  { key: "windy", label: "Windy", icon: Wind },
  { key: "extreme_heat", label: "Extreme Heat", icon: Flame },
];
export const WEATHER_BY_KEY = Object.fromEntries(WEATHER.map((w) => [w.key, w]));
export const weatherMeta = (key) => WEATHER_BY_KEY[key] || { key: "", label: "—", icon: CloudSun };

/** Delay severity — red is reserved for Critical (design system rule). */
export const SEVERITIES = [
  {
    key: "minor",
    label: "Minor",
    icon: CircleDot,
    cls: "bg-muted/30 text-muted-foreground border-border",
    dot: "bg-muted-foreground/60",
  },
  {
    key: "moderate",
    label: "Moderate",
    icon: TriangleAlert,
    cls: "border-amber-500/30 bg-amber-500/10 text-amber-300",
    dot: "bg-amber-400",
  },
  {
    key: "critical",
    label: "Critical",
    icon: AlertTriangle,
    cls: "border-[hsl(var(--destructive))]/35 bg-[hsl(var(--destructive))]/12 text-red-300",
    dot: "bg-red-400",
  },
];
export const SEVERITY_BY_KEY = Object.fromEntries(SEVERITIES.map((s) => [s.key, s]));
export const severityMeta = (key) => SEVERITY_BY_KEY[key] || null;

export const LOCK_HOURS = 48;
export const MAX_PHOTOS = 20;
export const MAX_INCIDENT_PHOTOS = 8;
export const WORK_CHARS = 2000;

export const todayStr = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** 2026-10-01 → Thursday 1 October 2026 */
export const fmtLongDate = (ymd) => {
  if (!ymd) return "";
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

/** ISO timestamp → 3 Oct 2026, 7:32 am */
export const fmtStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};

/** Hours left before the legal lock → "Locks in 1d 3h" / "Locks in 45 min" */
export const fmtLockCountdown = (hours) => {
  if (hours === null || hours === undefined) return "";
  if (hours <= 0) return "Locked";
  if (hours < 1) return `Locks in ${Math.max(1, Math.round(hours * 60))} min`;
  const total = Math.round(hours);
  const d = Math.floor(total / 24);
  const h = total - d * 24;
  if (d > 0) return `Locks in ${d}d ${h}h`;
  return `Locks in ${h}h`;
};

export const fmtTemp = (v) => (v === null || v === undefined || v === "" ? "—" : `${Number(v)}°C`);

export const newRowKey = () => Math.random().toString(36).slice(2, 10);

export const emptyWorker = () => ({ key: newRowKey(), name: "", trade: "", company: "", hours: "" });
export const emptyVisitor = () => ({ key: newRowKey(), name: "", company: "", reason: "" });
export const emptyPlant = () => ({ key: newRowKey(), name: "", notes: "" });

/** Build the blank form state (date auto-populated with today). */
export const blankEntryForm = () => ({
  date: todayStr(),
  weather_morning: "",
  weather_afternoon: "",
  temp_morning_c: "",
  temp_afternoon_c: "",
  workers: [emptyWorker()],
  work_completed: "",
  delays_issues: "",
  delay_severity: "",
  visitors: [],
  plant_equipment: [],
  has_incident: false,
  incident_description: "",
  incident_photos: [],
  photos: [],
  notes: "",
});

/** Stored entry → editable form state. */
export const entryToForm = (e) => ({
  date: e.date || todayStr(),
  weather_morning: e.weather_morning || "",
  weather_afternoon: e.weather_afternoon || "",
  temp_morning_c: e.temp_morning_c ?? "",
  temp_afternoon_c: e.temp_afternoon_c ?? "",
  workers: (e.workers || []).length ? e.workers.map((w) => ({ key: newRowKey(), name: w.name || "", trade: w.trade || "", company: w.company || "", hours: w.hours ?? "" })) : [emptyWorker()],
  work_completed: e.work_completed || "",
  delays_issues: e.delays_issues || "",
  delay_severity: e.delay_severity || "",
  visitors: (e.visitors || []).map((v) => ({ key: newRowKey(), name: v.name || "", company: v.company || "", reason: v.reason || "" })),
  plant_equipment: (e.plant_equipment || []).map((p) => ({ key: newRowKey(), name: p.name || "", notes: p.notes || "" })),
  has_incident: Boolean(e.has_incident),
  incident_description: e.incident_description || "",
  incident_photos: (e.incident_photos || []).map((p) => ({ key: newRowKey(), kind: "existing", name: p.name, caption: p.caption || "", path: `/site-diary/${e.id}/photos/${p.name}` })),
  photos: (e.photos || []).map((p) => ({ key: newRowKey(), kind: "existing", name: p.name, caption: p.caption || "", path: `/site-diary/${e.id}/photos/${p.name}` })),
  notes: e.notes || "",
});

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

/** Form state → API payload. Existing photos travel by name, new ones as base64. */
export const formToPayload = (f) => ({
  date: f.date,
  weather_morning: f.weather_morning,
  weather_afternoon: f.weather_afternoon,
  temp_morning_c: numOrNull(f.temp_morning_c),
  temp_afternoon_c: numOrNull(f.temp_afternoon_c),
  workers: f.workers.map((w) => ({ name: w.name.trim(), trade: (w.trade || "").trim(), company: w.company.trim(), hours: numOrNull(w.hours) })),
  work_completed: f.work_completed,
  delays_issues: f.delays_issues,
  delay_severity: f.delays_issues.trim() ? f.delay_severity : "",
  visitors: f.visitors.map((v) => ({ name: v.name.trim(), company: v.company.trim(), reason: v.reason.trim() })),
  plant_equipment: f.plant_equipment.map((p) => ({ name: p.name.trim(), notes: p.notes.trim() })),
  has_incident: f.has_incident,
  incident_description: f.has_incident ? f.incident_description : "",
  incident_photos: f.has_incident ? f.incident_photos.map((p) => (p.kind === "existing" ? { name: p.name, caption: p.caption.trim() } : { b64: p.b64, caption: p.caption.trim() })) : [],
  photos: f.photos.map((p) => (p.kind === "existing" ? { name: p.name, caption: p.caption.trim() } : { b64: p.b64, caption: p.caption.trim() })),
  notes: f.notes,
});
